import { AnimatePresence } from "framer-motion";
import { forwardRef, useMemo } from "react";
import { useKeysPress } from "../../hooks/useKeysPress/index.js";
import { usePortal } from "../../hooks/usePortal/index.js";
import { CloseLinear } from "../Iconsax/CloseLinear.js";
import { ICONSAX_SIZE } from "../Iconsax/Iconsax.types.js";
import { Text } from "../Text/Text.js";
import { TextSize, TextWeight } from "../Text/Text.types.js";
import { modalVariants, overlayVariants } from "./Modal.animations.js";
import { StyledContent, StyledHead, StyledModal, StyledModalBody, StyledModalOverlay, TitleContainer } from "./Modal.style.js";
import { DefaultModalProps, ModalProps } from "./Modal.types.js";

const outsideRootContainer = document.querySelector("body");

const ModalTitle = ({ title }: { title: ModalProps["title"] }): JSX.Element | null => {
    if (!title) return null;

    if (typeof title === "string") {
        return (
            <TitleContainer>
                <Text weight={TextWeight.BOLD} size={TextSize.SMALL}>
                    {title}
                </Text>
            </TitleContainer>
        );
    }

    return <TitleContainer>{title}</TitleContainer>;
};

/**
 * @deprecated This component should not be used directly.
 * Use {@link ModalV2} instead which provides the complete modal functionality.
 * This component will be removed in a future version.
 */
export const Modal = forwardRef<HTMLDivElement, ModalProps>((props, ref): JSX.Element => {
    const { children, isOpen, closeOnEscape, onClose, onModalClosed, onModalOpened, mountNode, ...rest } = props;

    const defaultProps: DefaultModalProps = {
        fluid: true,
        fluidContent: false,
        hideCloseButton: false,
        contentContainerOverflowYStyle: "auto",
        contentContainerOverflowXStyle: "auto",
        closeIconSize: ICONSAX_SIZE.LARGE,
        closeOnEscape: true,
        overflow: "hidden",
        modalBodyGap: "16",
        closeOnOverlayClick: true,
        overlayPointerEventsActivated: true,
    };
    const {
        hideCloseButton,
        fluidContent,
        title,
        modalBackground,
        contentContainerOverflowYStyle,
        contentContainerOverflowXStyle,
        closeIconSize,
        overflow,
        modalBodyGap,
        closeOnOverlayClick,
        overlayPointerEventsActivated,
        height,
        width,
        ...attributes
    } = {
        ...defaultProps,
        ...rest,
    };

    useKeysPress({
        sequences: [
            {
                handler: (_, event) => {
                    if (closeOnEscape && isOpen) {
                        onClose?.();
                        event.preventDefault();
                        event.stopPropagation();
                    }
                },
                keys: ["escape"],
            },
        ],
    });

    const showHead = useMemo(() => !!title || !hideCloseButton, [title, hideCloseButton]);

    const Portal = usePortal({
        mountNode: mountNode || outsideRootContainer!,
    });

    return (
        <Portal>
            <AnimatePresence>
                {isOpen && (
                    <>
                        <StyledModalOverlay
                            initial={overlayVariants.hidden}
                            animate={overlayVariants.shown}
                            exit={overlayVariants.hidden}
                            transition={{ duration: 0.3 }}
                            overlayPointerEventsActivated={overlayPointerEventsActivated}
                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                if (!closeOnOverlayClick) return;
                                onClose?.();
                                event.preventDefault();
                                event.stopPropagation();
                            }}
                        />
                        <StyledModal
                            initial={modalVariants.hidden}
                            animate={modalVariants.shown}
                            exit={modalVariants.hidden}
                            transition={{ duration: 0.3 }}
                            background={modalBackground}
                            ref={ref}
                            width={width}
                            height={height}
                            isOpen={isOpen}
                            overflow={overflow}
                            fluidContent={fluidContent}
                            onAnimationComplete={(variant: { opacity: number }) => {
                                if (variant === modalVariants.shown && onModalOpened) onModalOpened();
                                else if (variant === modalVariants.hidden && onModalClosed) onModalClosed();
                            }}
                            {...attributes}
                        >
                            <StyledModalBody gap={modalBodyGap}>
                                {showHead && (
                                    <StyledHead>
                                        <ModalTitle title={title} />
                                        {hideCloseButton === false && (
                                            <CloseLinear size={closeIconSize} onClick={onClose} style={{ cursor: "pointer" }} />
                                        )}
                                    </StyledHead>
                                )}

                                <StyledContent
                                    overflowYStyle={contentContainerOverflowYStyle}
                                    overflowXStyle={contentContainerOverflowXStyle}
                                    fluidContent={fluidContent}
                                    standalone={!showHead}
                                >
                                    {children}
                                </StyledContent>
                            </StyledModalBody>
                        </StyledModal>
                    </>
                )}
            </AnimatePresence>
        </Portal>
    );
});
